import { useApolloClient, useQuery } from '@apollo/client'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'

import { Button, CardBox, Container, NotificationDialog, Row, Stack, Text, useAlert } from '../../../components'
import * as CryptocurrencyPayment from '../../../components/payment/cryptocurrency'
import { mutations, queries } from '../../../graphql'
import { fromAmountField, getTrackingData } from '../../../utils'

export function Cryptocurrency() {
  const client = useApolloClient()
  const { fundId } = useParams()
  const { data: { me: { fund } = { fund: {} } } = {} } = useQuery(queries.funds.myFund, {
    variables: { fundId: Number(fundId) },
    skip: !fundId,
  })
  const { data: { me } = {} } = useQuery(queries.user.me)
  const { number } = fund
  const [{ Alert, alertProps }, { setAlert }] = useAlert()
  const {
    register,
    handleSubmit,
    formState,
    getValues,
    setValue,
    watch,
    reset: resetForm,
    control,
  } = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: {
      address: { province: '' },
      cryptocurrencies: [
        {
          cryptocurrencyType: '',
          walletAddress: '',
          quantity: 0,
          amount: 0,
          transactionHash: '',
          notes: '',
        },
      ],
      pageLanguageConfirmed: false,
    },
  })

  const { isSubmitting } = formState
  const [dialogOpen, setDialogOpen] = useState(false)

  const province = watch('address.province')
  useEffect(() => {
    setValue('address.province', getValues('address.province'))
  }, [province, setValue, getValues])

  const pageLanguageConfirmed = watch('pageLanguageConfirmed')

  const [cryptocurrencyPdf, setCryptocurrencyPdf] = useState(null)
  const createContributionRecords = async (data) => {
    try {
      if (data && Array.isArray(data.cryptocurrencies)) {
        const trackingData = getTrackingData()
        const promises = data.cryptocurrencies.map((crypto) =>
          client.mutate({
            mutation: mutations.contributions.createContributionRecord,
            variables: {
              fundId: Number(fundId),
              amount: Number(crypto.amount),
              paymentType: 13, // cryptocurrency payment type
              description:
                `${crypto.cryptocurrencyType} - ${crypto.quantity} Units${
                  crypto.transactionHash ? ` (TX: ${crypto.transactionHash})` : ''
                }${crypto.notes ? ` - ${crypto.notes}` : ''}` || 'unspecified cryptocurrency',
              taxReceipted: true,
              ...trackingData,
            },
            refetchQueries: [
              { query: queries.funds.myFund, variables: { fundId: Number(fundId) } },
              { query: queries.funds.myFundActivity, variables: { fundId: Number(fundId) } },
              { query: queries.paymentMethods.myLinkedBanks, variables: { fundId: Number(fundId) } },
            ],
            awaitRefetchQueries: true,
          })
        )
        const results = await Promise.all(promises)
        const ids = results.map((result) => result.data.createContributionRecord.id)

        setCryptocurrencyPdf(
          CryptocurrencyPayment.fetchGeneratedCryptocurrencyPdf({
            me,
            fundId,
            cryptocurrencies: data?.cryptocurrencies,
            exchangeInfo: data,
            contributionIds: ids,
          })
        )
        setDialogOpen(true)
      } else {
        throw new Error('Invalid cryptocurrency data')
      }
    } catch (e) {
      setAlert({
        message: `Failed to create a cryptocurrency contribution: ${e.message ?? ''}`,
        error: e.message,
        severity: 'error',
      })
    }
  }

  const onSubmit = async (data) => {
    const convertedData = {
      ...data,
      cryptocurrencies: data.cryptocurrencies.map((crypto) => ({
        ...crypto,
        quantity: parseFloat(fromAmountField(crypto.quantity)),
        amount: parseFloat(fromAmountField(crypto.amount)),
      })),
    }
    createContributionRecords(convertedData)
  }

  const closeSuccessDialog = () => {
    resetForm()
    setDialogOpen(false)
    setCryptocurrencyPdf(null)
  }

  return (
    <Container maxWidth="lg">
      <CardBox title="Cryptocurrency">
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack>
            <CryptocurrencyPayment.ExchangeInformation {...{ register, control, formState }} />
            <CryptocurrencyPayment.CryptocurrenciesToTransfer {...{ register, control, formState }} />
            <CryptocurrencyPayment.Acknowledgement {...{ control, formState }} />

            <Row sx={{ marginTop: '2rem!important' }} alignSelf="flex-end">
              <Button
                type="submit"
                disabled={isSubmitting || !pageLanguageConfirmed}
                data-testid="createCryptocurrencyContribution"
              >
                Submit
              </Button>
            </Row>
            <Alert {...alertProps} />
          </Stack>
        </form>
      </CardBox>

      <NotificationDialog
        open={dialogOpen}
        onClose={closeSuccessDialog}
        type="success"
        title="Success!"
        message={
          <>
            <Text.Body sx={{ mb: 2 }}>You have successfully initiated a cryptocurrency contribution.</Text.Body>
            <Text.Body sx={{ mb: 2 }}>
              To complete the contribution, you will now need to transfer the cryptocurrency to GiveWise&apos;s
              designated wallet address.
            </Text.Body>
            <Text.Body sx={{ mb: 2 }}>
              For your convenience, here is a link to download &quot;Cryptocurrency Transfer Instructions&quot; with the
              wallet addresses and transfer details.
            </Text.Body>

            <CryptocurrencyPayment.GeneratedCryptocurrencyInstructions me={me} cryptocurrencyPdf={cryptocurrencyPdf} />

            <Text.Body sx={{ mt: 2 }}>
              Please be aware that a tax receipt will not be issued until the cryptocurrency has been received and
              liquidated.
              <strong>
                {' '}
                NOTE: There may be significant adjustments due to cryptocurrency market volatility and network fees.
              </strong>
            </Text.Body>
          </>
        }
        actions={[{ label: 'Done', onClick: closeSuccessDialog, to: `/funds/${fundId}/activity/giving-wallet` }]}
      />
    </Container>
  )
}
